import {html, HTMLTemplateResult, nothing, PropertyValues} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {translate} from "lit-translate";
import {container} from "tsyringe";

import {KnowledgeObject} from "../../../domain/entities/knowledge-object.entity";
import {KnowledgeObjectRepository} from "../../../domain/repositories/knowledge-object.repository";
import {AbstractLitElement} from "../../abstract-lit.element";

/** Shape of the single content item an `image` KnowledgeObject carries. */
interface ImageContentItem {
    kind?: string;
    assetUid?: string;
    mime?: string;
    sizeBytes?: number;
}

/**
 * Image rendering pane for a single KnowledgeObject of type `image`.
 *
 * Loads the KnowledgeObject's content lazily when `item` changes and renders an
 * `<img>` pointing at the tenant-scoped download endpoint
 * (`GET /api/v1/files/{assetUid}`). Out-of-order load resolutions are discarded
 * via a generation counter, re-checked at all three sites (success, catch,
 * finally) exactly as `knowledge-whiteboard.component.ts:121,129,137`.
 *
 * The payload is treated as untrusted: only a content item that BOTH declares
 * `kind === 'image'` AND carries an `assetUid` produces an `<img>`. Every other
 * shape — an empty payload array, a missing content row, a half-written item —
 * renders a graceful empty state instead of a request for
 * `/api/v1/files/undefined`. Light DOM per ADR-007.
 */
@customElement('knowledge-image')
export class KnowledgeImage extends AbstractLitElement {

    @property({type: Object})
    public item: KnowledgeObject | null = null;

    /** The guarded asset uid — non-null ONLY for a well-formed image payload. */
    @state()
    public _assetUid: string | null = null;

    @state()
    public _isLoading = false;

    /** Set when the browser fails to fetch/decode the image (DD-3 placeholder). */
    @state()
    public _hasImageError = false;

    /** Track item identity across willUpdate calls to skip redundant loads. */
    private _lastItemUid: string | null = null;

    /**
     * Monotonic counter incremented on every `item` change. Each in-flight
     * `findOneByIdWithContent` call captures the value at dispatch time and
     * discards its result if the counter has moved on (stale-promise guard).
     */
    private _loadGeneration = 0;

    public async willUpdate(changedProperties: PropertyValues): Promise<void> {
        if (!changedProperties.has('item')) {
            return;
        }
        const item = this.item;
        if (!item) {
            this._assetUid = null;
            this._isLoading = false;
            this._hasImageError = false;
            this._lastItemUid = null;
            return;
        }
        if (item.uid === this._lastItemUid) {
            return;
        }

        // Increment BEFORE any await so that subsequent calls invalidate this one.
        this._loadGeneration += 1;
        const myGeneration = this._loadGeneration;
        this._isLoading = true;
        this._assetUid = null;
        this._hasImageError = false;

        const repository = container.resolve(KnowledgeObjectRepository);
        try {
            const loaded = await repository.findOneByIdWithContent(item.uid);
            if (myGeneration !== this._loadGeneration) {
                // A newer load has been kicked off — discard this stale result.
                return;
            }
            this._assetUid = KnowledgeImage._extractAssetUid(loaded);
            this._lastItemUid = item.uid;
        } catch (error) {
            if (myGeneration !== this._loadGeneration) {
                return;
            }
            console.error(`Failed to load image content for «${item.uid}».`, error);
            this._assetUid = null;
            this._lastItemUid = item.uid;
        } finally {
            if (myGeneration === this._loadGeneration) {
                this._isLoading = false;
            }
        }
    }

    /**
     * Returns the asset uid only when the first content item is genuinely an
     * image reference. Both conditions are load-bearing: the `kind`
     * discriminator keeps a foreign payload from being rendered as an image, and
     * the uid check keeps a half-written item from producing
     * `src="/api/v1/files/undefined"`.
     */
    private static _extractAssetUid(loaded: unknown): string | null {
        const payload = (loaded as { content?: { payload?: unknown } } | null)
            ?.content?.payload;
        if (!Array.isArray(payload)) {
            return null;
        }
        const first = payload[0] as ImageContentItem | undefined;
        if (first?.kind !== 'image' || !first.assetUid) {
            return null;
        }
        return first.assetUid;
    }

    /** Swaps the broken `<img>` for the load-error placeholder (DD-3). */
    public _onImageError = (): void => {
        this._hasImageError = true;
    };

    /** @internal Public only so unit tests can walk the rendered template; Lit itself calls it. */
    public render(): HTMLTemplateResult | typeof nothing {
        if (!this.item) {
            return nothing;
        }

        // The loading branch MUST precede the empty branch: while a load is in
        // flight `_assetUid` is null, so checking emptiness first would tell the
        // user the object has no image and then snap to the picture.
        if (this._isLoading) {
            return html`<div class="knowledge-image__loading">${translate('wissen.image.loading')}</div>`;
        }

        if (this._assetUid === null) {
            return html`<div class="knowledge-image__empty">${translate('wissen.image.empty')}</div>`;
        }

        return this._hasImageError
            ? html`<div class="knowledge-image__error">${translate('wissen.image.load-error')}</div>`
            : html`<img class="knowledge-image__img"
                        src=${`/api/v1/files/${this._assetUid}`}
                        alt=${this.item.title ?? ''}
                        @error=${this._onImageError}
            >`;
    }
}
